import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookSelector } from "./BookSelector";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";

interface CreateBuddyReadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateBuddyRead: (input: {
    book_id: string;
    title?: string;
    start_date?: string;
    target_end_date?: string;
    member_ids: string[];
  }) => Promise<any>;
}

export function CreateBuddyReadDialog({
  open,
  onOpenChange,
  onCreateBuddyRead
}: CreateBuddyReadDialogProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<'book' | 'friends' | 'details'>('book');
  const [selectedBook, setSelectedBook] = useState<any>(null);
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [creating, setCreating] = useState(false);

  // Fetch user's friends (following)
  const { data: friends } = useQuery({
    queryKey: ['friends', user?.id],
    enabled: !!user && open,
    queryFn: async () => {
      const { data: following, error: err1 } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user!.id)
        .eq('status', 'accepted');

      const { data: followers, error: err2 } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', user!.id)
        .eq('status', 'accepted');

      if (err1) throw err1;
      if (err2) throw err2;

      const ids = Array.from(new Set<string>([
        ...(following?.map((r: any) => r.following_id) || []),
        ...(followers?.map((r: any) => r.follower_id) || []),
      ].filter(Boolean)));

      // Exclude current user just in case
      const filteredIds = ids.filter((id) => id !== user!.id);
      if (filteredIds.length === 0) return [];

      const { data: profiles, error: rpcError } = await supabase.rpc('get_profiles_by_ids', {
        user_ids: filteredIds,
      });

      if (rpcError) throw rpcError;

      return profiles || [];
    }
  });

  const handleBookSelect = (book: any) => {
    setSelectedBook(book);
    setStep('friends');
  };

  const handleFriendsNext = () => {
    setStep('details');
  };

  const handleCreate = async () => {
    if (!selectedBook) return;

    setCreating(true);
    try {
      await onCreateBuddyRead({
        book_id: selectedBook.id,
        title: title || undefined,
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        target_end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        member_ids: selectedFriends
      });

      // Reset form
      setSelectedBook(null);
      setSelectedFriends([]);
      setTitle("");
      setStartDate(undefined);
      setEndDate(undefined);
      setStep('book');
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating buddy read:', error);
    } finally {
      setCreating(false);
    }
  };

  const toggleFriend = (friendId: string) => {
    setSelectedFriends(prev =>
      prev.includes(friendId)
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {step === 'book' && 'Select a Book'}
            {step === 'friends' && 'Invite Friends'}
            {step === 'details' && 'Buddy Read Details'}
          </DialogTitle>
        </DialogHeader>

        {step === 'book' && (
          <div className="space-y-4">
            <BookSelector
              open={true}
              onClose={() => onOpenChange(false)}
              onSelect={handleBookSelect}
            />
          </div>
        )}

        {step === 'friends' && (
          <div className="space-y-4">
            {selectedBook && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <img
                  src={selectedBook.cover_url || '/placeholder.svg'}
                  alt={selectedBook.title}
                  className="w-12 h-16 object-cover rounded"
                />
                <div className="flex-1">
                  <p className="font-medium">{selectedBook.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedBook.authors?.join(', ')}
                  </p>
                </div>
              </div>
            )}

            <div>
              <Label>Select Friends to Invite</Label>
              <ScrollArea className="h-[300px] mt-2 border rounded-lg p-2">
                {friends && friends.length > 0 ? (
                  <div className="space-y-2">
                    {friends.map((friend: any) => (
                      <div
                        key={friend.id}
                        className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg cursor-pointer"
                        onClick={() => toggleFriend(friend.id)}
                      >
                        <Checkbox
                          checked={selectedFriends.includes(friend.id)}
                          onCheckedChange={() => toggleFriend(friend.id)}
                        />
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={friend.image} />
                          <AvatarFallback>
                            {friend.username?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">
                            {friend.name || friend.username}
                          </p>
                          {friend.name && (
                            <p className="text-xs text-muted-foreground">
                              @{friend.username}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No friends to invite. Follow some users first!
                  </p>
                )}
              </ScrollArea>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('book')}>
                Back
              </Button>
              <Button
                onClick={handleFriendsNext}
                disabled={selectedFriends.length === 0}
                className="flex-1"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {step === 'details' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Buddy Read Title (Optional)</Label>
              <Input
                id="title"
                placeholder="e.g., Summer Reading Challenge"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>Target End Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                      disabled={(date) =>
                        startDate ? date < startDate : false
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('friends')}>
                Back
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1"
              >
                {creating ? 'Creating...' : 'Create Buddy Read'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
